import { ENDPOINTS } from 'config/endpoints';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import CronoError from 'crono-fe-common/types/error';
import client from 'utils/clients/client';
import { Request } from 'crono-fe-common/types/request';
import { ResponsePaginated } from 'crono-fe-common/types/response';
import { StrategySearch } from 'crono-fe-common/types/DTO/strategySearch';
import { StrategyWithAnalytics } from 'crono-fe-common/types/strategy';

export default function useSearchStrategies(
  searchParameters: StrategySearch,
  enabled = true,
  refetch = false,
) {
  const request: Request = {
    url: ENDPOINTS.strategy.search,
    config: {
      method: 'post',
      data: { data: searchParameters },
    },
  };
  const queryString = JSON.stringify({
    ...searchParameters,
    enableCache: refetch,
  });
  const requestConfig = {
    queryKey: [ENDPOINTS.strategy.search, queryString],

    queryFn: () => client(request),
    enabled,
    staleTime: refetch ? undefined : 30 * 60 * 1000,
    refetchInterval: refetch ? 5_000 : undefined,
  };

  return useQuery<ResponsePaginated<StrategyWithAnalytics[]>, CronoError>(
    requestConfig,
  );
}

export function useSearchStrategiesInfiniteScroll(
  searchParameters: Omit<StrategySearch, 'offset'>,
  enabled = true,
) {
  const requestConfig = {
    queryKey: [
      ENDPOINTS.strategy.search,
      'infinite',
      JSON.stringify(searchParameters),
    ],
    queryFn: ({ pageParam = 0 }) => {
      const request: Request = {
        url: ENDPOINTS.strategy.search,
        config: {
          method: 'post',
          data: { data: { ...searchParameters, offset: pageParam } },
        },
      };
      return client(request);
    },
    getNextPageParam: (
      lastPage: ResponsePaginated<StrategyWithAnalytics[]>,
      pages: ResponsePaginated<StrategyWithAnalytics[]>[],
    ) => {
      const howManyLoaded = pages.reduce(
        (sum, page) => sum + page.data.data.length,
        0,
      );

      const checkIfHasNextPage =
        lastPage.data.data.length === 0 ||
        lastPage.data.data.length < searchParameters.limit;

      if (checkIfHasNextPage) {
        return undefined;
      }

      return howManyLoaded;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    enabled,
  };

  return useInfiniteQuery<
    ResponsePaginated<StrategyWithAnalytics[]>,
    CronoError
  >(requestConfig);
}
